import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Buttons/Button";
import {
  createAssignmentReview,
  createAssignmentRevision,
  fetchAssignmentResponseById,
  fetchAssignmentResponseNavigation,
} from "../../services/assignmentResponseService/assignmentResponseService";
import { useNotify } from "context/NotificationContext";
import {
  fileIcon as FileIconGlyph,
  linkIcon as LinkIconGlyph,
  videoFileIcon as VideoIconGlyph,
} from "assets/icons";
import { logger } from "utils/logger";
import AssignmentTestAttemptResult from "./AssignmentTestAttemptResult";
import {
  AssignmentResponseDetail,
  AssignmentResponseNavigation,
} from "./AssignmentResponseDetailPage.types";
import {
  appendReviewTemplate,
  attachmentCountLabel,
  formatResponseCreated,
  getReviewActionAvailability,
  REVIEW_TEMPLATES,
} from "./assignmentReview.utils";
import {
  AnswerText,
  AssignmentResponsePageContainer,
  AttachmentIcon,
  AttachmentLink,
  AttachmentName,
  AttachmentsGrid,
  BackLink,
  Card,
  CardBody,
  CardCounter,
  CardHeader,
  CardTitle,
  CommentInput,
  ContentWrapper,
  DoneBox,
  DoneRating,
  DoneRow,
  DoneText,
  EditActions,
  FieldLabel,
  Hint,
  MainColumn,
  NavigationButton,
  PageHeader,
  PageInner,
  PageState,
  PageTitle,
  PageToolbar,
  QueueCount,
  QueueNavigation,
  RatingButton,
  RatingGroup,
  RatingNote,
  ReviewActions,
  ReviewField,
  ReviewGrid,
  ReviewPanel,
  ReviewPanelBody,
  SentNote,
  StatusBadge,
  StudentMeta,
  StudentName,
  StudentRow,
  StudentSide,
  TaskText,
  TemplateChip,
  Templates,
  TextAction,
  TitleRow,
} from "./AssignmentResponseDetailPage.styles";

const EMPTY_NAVIGATION: AssignmentResponseNavigation = {
  previous_id: null,
  next_id: null,
  unreviewed_count: 0,
};

type ReviewSubmitAction = "approve" | "revision";

const AssignmentResponseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [data, setData] = useState<AssignmentResponseDetail | null>(null);
  const [navigation, setNavigation] = useState<AssignmentResponseNavigation>(EMPTY_NAVIGATION);
  const [loading, setLoading] = useState(true);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState(false);
  const [editingReview, setEditingReview] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<ReviewSubmitAction | null>(null);

  const responseId = Number(id);

  const loadNavigation = useCallback(async () => {
    if (!Number.isFinite(responseId)) {
      setNavigation(EMPTY_NAVIGATION);
      return;
    }

    setNavigationLoading(true);
    try {
      const response = await fetchAssignmentResponseNavigation(responseId);
      setNavigation(response);
    } catch (error) {
      logger.error("Ошибка загрузки очереди домашних заданий:", error);
      setNavigation(EMPTY_NAVIGATION);
    } finally {
      setNavigationLoading(false);
    }
  }, [responseId]);

  const load = useCallback(async () => {
    if (!Number.isFinite(responseId)) {
      setData(null);
      setNavigation(EMPTY_NAVIGATION);
      setLoadError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetchAssignmentResponseById(responseId);
      setData(response);
      setRating(response.assignment_review?.rating || 0);
      setComment(response.assignment_review?.teacher_comments || "");
      setCommentError(false);
      setEditingReview(false);
      await loadNavigation();
    } catch (error) {
      logger.error("Ошибка загрузки ответа на домашнее задание:", error);
      setData(null);
      setNavigation(EMPTY_NAVIGATION);
      setLoadError(true);
      notify.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить работу ученика",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [loadNavigation, notify, responseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const attachmentsCount = useMemo(
    () => (data?.files_from_storage.length || 0) + (data?.video_url ? 1 : 0),
    [data],
  );

  const actionAvailability = useMemo(
    () => getReviewActionAvailability(comment, rating),
    [comment, rating],
  );

  const handleRating = (value: number) => {
    setRating((current) => (current === value ? 0 : value));
  };

  const handleCommentChange = (value: string) => {
    setComment(value);
    if (value.trim()) setCommentError(false);
  };

  const handleTemplate = (template: string) => {
    setComment((current) => appendReviewTemplate(current, template));
    setCommentError(false);
  };

  const handleSubmit = async (action: ReviewSubmitAction) => {
    if (!data || submittingAction) return;

    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      setCommentError(true);
      return;
    }
    if (action === "approve" && rating <= 0) return;

    setSubmittingAction(action);
    try {
      if (action === "approve") {
        await createAssignmentReview({
          response: data.id,
          teacher_comments: trimmedComment,
          rating,
        });
      } else {
        await createAssignmentRevision({
          response: data.id,
          teacher_comments: trimmedComment,
        });
      }

      notify.show({
        severity: "success",
        summary: action === "approve" ? "Задание принято" : "Отправлено на доработку",
        detail:
          action === "approve"
            ? "Проверка отправлена ученику"
            : "Ученик получил комментарий и запрос на доработку",
        life: 3000,
      });
      await load();
    } catch (error: any) {
      notify.show({
        severity: "error",
        summary: "Ошибка",
        detail: error.response?.data?.detail || "Не удалось отправить проверку",
        life: 3000,
      });
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleEditReview = () => {
    if (!data?.assignment_review) return;
    setRating(data.assignment_review.rating || 0);
    setComment(data.assignment_review.teacher_comments || "");
    setCommentError(false);
    setEditingReview(true);
  };

  const cancelEditReview = () => {
    setEditingReview(false);
    setRating(data?.assignment_review?.rating || 0);
    setComment(data?.assignment_review?.teacher_comments || "");
    setCommentError(false);
  };

  const openQueueItem = (targetId: number | null) => {
    if (!targetId || navigationLoading || submittingAction) return;
    navigate(`/assignment-responses/${targetId}`);
  };

  if (loading) {
    return (
      <AssignmentResponsePageContainer>
        <Sidebar />
        <ContentWrapper>
          <PageInner>
            <PageState>Загружаем работу ученика…</PageState>
          </PageInner>
        </ContentWrapper>
      </AssignmentResponsePageContainer>
    );
  }

  if (!data || loadError) {
    return (
      <AssignmentResponsePageContainer>
        <Sidebar />
        <ContentWrapper>
          <PageInner>
            <BackLink type="button" onClick={() => navigate("/homework")}>
              <span aria-hidden="true">←</span>
              Все домашние задания
            </BackLink>
            <PageState>
              Не удалось загрузить работу. <TextAction onClick={() => void load()}>Повторить</TextAction>
            </PageState>
          </PageInner>
        </ContentWrapper>
      </AssignmentResponsePageContainer>
    );
  }

  const hasReview = data.is_reviewed && Boolean(data.assignment_review);
  const showReviewForm = !hasReview || editingReview;
  const submittedAt = formatResponseCreated(data.created);
  const busy = Boolean(submittingAction);

  return (
    <AssignmentResponsePageContainer>
      <Sidebar />
      <ContentWrapper>
        <PageInner>
          <Breadcrumbs
            items={[
              { label: "Домашние задания", to: "/homework" },
              {
                label: data.course.title,
                to: `/courses/${data.course.id}/lessons`,
              },
              {
                label: `${data.lesson.title} · ${data.assignment.title}`,
              },
            ]}
          />
          <PageToolbar>
            <BackLink type="button" onClick={() => navigate("/homework")}>
              <span aria-hidden="true">←</span>
              Все домашние задания
            </BackLink>
            <QueueNavigation>
              <QueueCount>Непроверенных: {navigation.unreviewed_count}</QueueCount>
              <NavigationButton
                type="button"
                disabled={!navigation.previous_id || navigationLoading || busy}
                onClick={() => openQueueItem(navigation.previous_id)}
              >
                ← Предыдущее
              </NavigationButton>
              <NavigationButton
                type="button"
                disabled={!navigation.next_id || navigationLoading || busy}
                onClick={() => openQueueItem(navigation.next_id)}
              >
                Следующее →
              </NavigationButton>
            </QueueNavigation>
          </PageToolbar>

          <PageHeader>
            <div>
              <TitleRow>
                <PageTitle>{data.assignment.title}</PageTitle>
                <StatusBadge $done={data.is_reviewed}>
                  {data.is_reviewed ? "Проверено" : "Не проверено"}
                </StatusBadge>
              </TitleRow>
            </div>
          </PageHeader>

          <ReviewGrid>
            <MainColumn>
              <Card>
                <StudentRow>
                  <div>
                    <StudentName>{data.student.full_name}</StudentName>
                    <StudentMeta>
                      {data.student.user_name && (
                        <a
                          href={`https://t.me/${data.student.user_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          @{data.student.user_name}
                        </a>
                      )}
                      {data.student.phone_number && <span>{data.student.phone_number}</span>}
                    </StudentMeta>
                  </div>
                  <StudentSide>Отправлено {submittedAt}</StudentSide>
                </StudentRow>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Ответ ученика</CardTitle>
                  {attachmentsCount > 0 && (
                    <CardCounter>{attachmentCountLabel(attachmentsCount)}</CardCounter>
                  )}
                </CardHeader>
                <CardBody>
                  <AnswerText $empty={!data.text}>
                    {data.text || (data.test_attempt
                      ? "Текстовый комментарий не добавлен."
                      : "Ученик прислал только вложения, без текста.")}
                  </AnswerText>

                  {attachmentsCount > 0 && (
                    <AttachmentsGrid>
                      {data.files_from_storage.map((file) => {
                        const Glyph = file.type === "video" ? VideoIconGlyph : FileIconGlyph;
                        return (
                          <AttachmentLink
                            key={file.id}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={file.name}
                          >
                            <AttachmentIcon>
                              <Glyph aria-hidden="true" />
                            </AttachmentIcon>
                            <AttachmentName>{file.name}</AttachmentName>
                          </AttachmentLink>
                        );
                      })}

                      {data.video_url && (
                        <AttachmentLink
                          href={data.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={data.video_url}
                        >
                          <AttachmentIcon>
                            <LinkIconGlyph aria-hidden="true" />
                          </AttachmentIcon>
                          <AttachmentName>{data.video_url}</AttachmentName>
                        </AttachmentLink>
                      )}
                    </AttachmentsGrid>
                  )}

                  {data.test_attempt ? (
                    <AssignmentTestAttemptResult attempt={data.test_attempt} />
                  ) : null}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Текст задания</CardTitle>
                  <TextAction
                    type="button"
                    onClick={() =>
                      navigate(`/courses/${data.course.id}/lessons/${data.lesson.id}/assignments`)
                    }
                  >
                    Открыть задания урока
                  </TextAction>
                </CardHeader>
                <CardBody>
                  <TaskText>{data.assignment.content || "Текст задания не указан."}</TaskText>
                </CardBody>
              </Card>
            </MainColumn>

            <ReviewPanel>
              <Card>
                <CardHeader>
                  <CardTitle>{hasReview && !editingReview ? "Проверено" : "Проверка"}</CardTitle>
                </CardHeader>
                <ReviewPanelBody>
                  {showReviewForm ? (
                    <>
                      <FieldLabel as="span">Оценка</FieldLabel>
                      <RatingGroup>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <RatingButton
                            key={value}
                            type="button"
                            $selected={rating === value}
                            aria-pressed={rating === value}
                            onClick={() => handleRating(value)}
                            disabled={busy}
                          >
                            {value}
                          </RatingButton>
                        ))}
                      </RatingGroup>
                      <RatingNote>
                        Для подтверждения задания выберите оценку. Для отправки на доработку оценка не нужна.
                      </RatingNote>

                      <ReviewField>
                        <FieldLabel htmlFor="assignment-review-comment">Комментарий ученику</FieldLabel>
                        <CommentInput
                          id="assignment-review-comment"
                          value={comment}
                          $error={commentError}
                          placeholder="Что получилось, что стоит поправить"
                          onChange={(event) => handleCommentChange(event.target.value)}
                          disabled={busy}
                        />
                        <Hint $error={commentError}>
                          {commentError
                            ? "Комментарий обязателен, ученику придёт именно он."
                            : "Ученик получит комментарий в боте."}
                        </Hint>
                        <Templates>
                          {REVIEW_TEMPLATES.map((template) => (
                            <TemplateChip
                              key={template}
                              type="button"
                              onClick={() => handleTemplate(template)}
                              disabled={busy}
                            >
                              {template}
                            </TemplateChip>
                          ))}
                        </Templates>
                      </ReviewField>

                      {editingReview && (
                        <EditActions>
                          <TextAction type="button" onClick={cancelEditReview} disabled={busy}>
                            Отменить изменения
                          </TextAction>
                        </EditActions>
                      )}

                      <ReviewActions>
                        <Button
                          variant="secondary"
                          loading={submittingAction === "revision"}
                          disabled={busy || !actionAvailability.canRequestRevision}
                          onClick={() => void handleSubmit("revision")}
                        >
                          Отправить на доработку
                        </Button>
                        <Button
                          variant="primary"
                          loading={submittingAction === "approve"}
                          disabled={busy || !actionAvailability.canApprove}
                          onClick={() => void handleSubmit("approve")}
                        >
                          Подтвердить задание
                        </Button>
                      </ReviewActions>
                      <SentNote>
                        Доработка отправляется без оценки. Подтверждение сохраняет выбранную оценку и завершает проверку.
                      </SentNote>
                    </>
                  ) : (
                    <>
                      <DoneBox>
                        <DoneRow>
                          <DoneRating>
                            Оценка:{" "}
                            <strong>
                              {data.assignment_review?.rating
                                ? `${data.assignment_review.rating} из 5`
                                : "без оценки"}
                            </strong>
                          </DoneRating>
                        </DoneRow>
                        <DoneText>{data.assignment_review?.teacher_comments}</DoneText>
                      </DoneBox>
                      <ReviewActions>
                        <Button variant="secondary" onClick={handleEditReview}>
                          Изменить проверку
                        </Button>
                      </ReviewActions>
                    </>
                  )}
                </ReviewPanelBody>
              </Card>
            </ReviewPanel>
          </ReviewGrid>
        </PageInner>
      </ContentWrapper>
    </AssignmentResponsePageContainer>
  );
};

export default AssignmentResponseDetailPage;
